import { API_URL } from '@config/api';
import { ApiError, ApiErrorCode } from '@/types/api';
import { User } from '@features/auth/types';
import { getAuthHeaders } from '@config/api';

interface LoginCredentials {
  email: string;
  password: string;
}

interface RecoveryPasswordCredentials {
  email: string;
}

interface CheckCodePasswordCredentials {
  email: string;
  code: string;
}

interface ChangePasswordCredentials {
  email: string;
  code: string;
  password: string;
  password_confirm: string;
}

interface LoginResponse {
  token: string;
}



interface RecoveryPasswordResponse {
  status: string;
}

export const authService = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    try {
      const response = await fetch(`${API_URL}/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Create a properly structured ApiError
        const apiError: ApiError = {
          code: errorData.code || ApiErrorCode.UNAUTHORIZED,
          message: errorData.message || 'Credenciais inválidas',
          statusCode: response.status,
          timestamp: new Date().toISOString(),
          path: `${API_URL}/auth`,
          method: 'POST',
          validationErrors: errorData.validationErrors,
          details: errorData.details || {
            requestId: response.headers.get('x-request-id') || undefined,
          }
        };
        
        throw apiError;
      }

      return response.json();
    } catch (error) {
      // If the error is already an ApiError, rethrow it
      if ((error as ApiError).code) {
        throw error;
      }
      
      // Otherwise, create a generic ApiError
      const apiError: ApiError = {
        code: ApiErrorCode.UNKNOWN_ERROR,
        message: (error as Error).message || 'Erro desconhecido ao fazer login',
        statusCode: 500,
        timestamp: new Date().toISOString(),
        path: `${API_URL}/auth`,
        method: 'POST'
      };
      
      throw apiError;
    }
  },

   recoveryPassword: async (credentials: RecoveryPasswordCredentials): Promise<RecoveryPasswordResponse> => {
    try {
      const response = await fetch(`${API_URL}/recovery_password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Create a properly structured ApiError
        const apiError: ApiError = {
          code: errorData.code || ApiErrorCode.UNAUTHORIZED,
          message: errorData.message || 'Credenciais inválidas',
          statusCode: response.status,
          timestamp: new Date().toISOString(),
          path: `${API_URL}/auth`,
          method: 'POST',
          validationErrors: errorData.validationErrors,
          details: errorData.details || {
            requestId: response.headers.get('x-request-id') || undefined,
          }
        };
        
        throw apiError;
      }

      return response.json();
    } catch (error) {
      // If the error is already an ApiError, rethrow it
      if ((error as ApiError).code) {
        throw error;
      }
      
      // Otherwise, create a generic ApiError
      const apiError: ApiError = {
        code: ApiErrorCode.UNKNOWN_ERROR,
        message: (error as Error).message || 'Erro desconhecido ao fazer login',
        statusCode: 500,
        timestamp: new Date().toISOString(),
        path: `${API_URL}/auth`,
        method: 'POST'
      };
      
      throw apiError;
    }
  },
  checkCodePassword: async (credentials: CheckCodePasswordCredentials): Promise<RecoveryPasswordResponse> => {
    try {
      const response = await fetch(`${API_URL}/check_code_password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Create a properly structured ApiError
        const apiError: ApiError = {
          code: errorData.code || ApiErrorCode.UNAUTHORIZED,
          message: errorData.message || 'Credenciais inválidas',
          statusCode: response.status,
          timestamp: new Date().toISOString(),
          path: `${API_URL}/auth`,
          method: 'POST',
          validationErrors: errorData.validationErrors,
          details: errorData.details || {
            requestId: response.headers.get('x-request-id') || undefined,
          }
        };
        
        throw apiError;
      }

      return response.json();
    } catch (error) {
      // If the error is already an ApiError, rethrow it
      if ((error as ApiError).code) {
        throw error;
      }
      
      // Otherwise, create a generic ApiError
      const apiError: ApiError = {
        code: ApiErrorCode.UNKNOWN_ERROR,
        message: (error as Error).message || 'Erro desconhecido ao fazer login',
        statusCode: 500,
        timestamp: new Date().toISOString(),
        path: `${API_URL}/auth`,
        method: 'POST'
      };
      
      throw apiError;
    }
  },


  changePasswordRecovery: async (credentials: ChangePasswordCredentials): Promise<RecoveryPasswordResponse> => {
    try {
      const response = await fetch(`${API_URL}/change_password_recovery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Create a properly structured ApiError
        const apiError: ApiError = {
          code: errorData.code || ApiErrorCode.UNAUTHORIZED,
          message: errorData.message || 'Credenciais inválidas',
          statusCode: response.status,
          timestamp: new Date().toISOString(),
          path: `${API_URL}/auth`,
          method: 'POST',
          validationErrors: errorData.validationErrors,
          details: errorData.details || {
            requestId: response.headers.get('x-request-id') || undefined,
          }
        };
        
        throw apiError;
      }

      return response.json();
    } catch (error) {
      // If the error is already an ApiError, rethrow it
      if ((error as ApiError).code) {
        throw error;
      }
      
      // Otherwise, create a generic ApiError
      const apiError: ApiError = {
        code: ApiErrorCode.UNKNOWN_ERROR,
        message: (error as Error).message || 'Erro desconhecido ao fazer login',
        statusCode: 500,
        timestamp: new Date().toISOString(),
        path: `${API_URL}/auth`,
        method: 'POST'
      };
      
      throw apiError;
    }
  },

  fetchUserProfile: async (): Promise<User> => {
    try {
      const response = await fetch(`${API_URL}/me`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Create a properly structured ApiError
        const apiError: ApiError = {
          code: errorData.code || ApiErrorCode.UNAUTHORIZED,
          message: errorData.message || 'Erro ao obter perfil do usuário',
          statusCode: response.status,
          timestamp: new Date().toISOString(),
          path: `${API_URL}/me`,
          method: 'GET',
          validationErrors: errorData.validationErrors,
          details: errorData.details || {
            requestId: response.headers.get('x-request-id') || undefined,
          }
        };
        
        throw apiError;
      }

      const profile = await response.json();

      // RESTAURACAO item 2 ata: enrich /me com permissions de /users/me (canViewAll etc).
      // /me nao retorna permissions, /users/me retorna. Merge silencioso — se /users/me
      // falhar, retorna apenas o profile basico.
      try {
        const permsResp = await fetch(`${API_URL}/users/me`, {
          method: 'GET',
          headers: getAuthHeaders(),
        });
        if (permsResp.ok) {
          const permsData = await permsResp.json();
          if (permsData?.permissions && profile?.data) {
            profile.data.permissions = permsData.permissions;
          }
        }
      } catch {
        // silencia: permissions e enriquecimento opcional
      }

      return profile;
    } catch (error) {
      // If the error is already an ApiError, rethrow it
      if ((error as ApiError).code) {
        throw error;
      }

      // Otherwise, create a generic ApiError
      const apiError: ApiError = {
        code: ApiErrorCode.UNKNOWN_ERROR,
        message: (error as Error).message || 'Erro desconhecido ao obter perfil do usuário',
        statusCode: 500,
        timestamp: new Date().toISOString(),
        path: `${API_URL}/me`,
        method: 'GET'
      };

      throw apiError;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
  },

  getToken: () => {
    return localStorage.getItem('token');
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  handleUnauthorized: () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  }
};
  